import { useState, useCallback, useRef } from "react";
import { FiPlus } from "react-icons/fi";
import { useDisclosure, useToast, Text } from "@chakra-ui/react";
import SkeletonBase from "newui/components/Skeleton";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { buildEvaluationPanelSteps } from "guides/features/Evaluation";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useEvaluationPanel } from "../../../hooks/useEvaluationPanel";
import type { PanelMode, SendInvitationsPayload } from "../../../types/panel.type";
import { PanelModeSelector } from "./PanelModeSelector";
import { PanelMemberTable } from "./PanelMemberTable";
import { TOUR_PREVIEW_MEMBERS } from "./tourPreviewMembers";
import { AddPanelMemberRow } from "./AddPanelMemberRow";
import { InviteConfirmationDrawer } from "../../InviteConfirmationDrawer";
import { PanelMemberSummary } from "./PanelMemberSummary";
import { useCurrentCompany, useCurrentUserId } from "hooks/auth";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { InfoBox, WarningBox } from "newui/components/IconBox";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { RfxLeadInfoBox } from "../InitialEvaluationStep/panel";

interface EvaluationPanelStepProps {
	disclaimerEnabled?: boolean;
	evalId?: number;
	isLoading?: boolean;
	initialEvaluationReached?: boolean;
	isRfxLead?: boolean;
	rfxData: Rfx;
	savedPanelMode?: PanelMode;
	onBeforeNext?: (check: () => Promise<boolean>) => void;
	onPanelModeChange?: (mode: PanelMode) => void;
}

export const EvaluationPanelStep: React.FC<EvaluationPanelStepProps> = ({
	rfxData,
	evalId,
	initialEvaluationReached = false,
	isLoading = false,
	isRfxLead = false,
	disclaimerEnabled = false,
	savedPanelMode,
	onPanelModeChange,
	onBeforeNext,
}) => {
	const rfxId = rfxData?.id;
	const hasAIAccess = useHasAIEvaluationAccess();
	const currentUserId = useCurrentUserId();
	const { id: companyId, name: companyName } = useCurrentCompany();
	const rfxTitle = rfxData?.name ?? "";
	const toast = useToast();
	const { clientPaths } = useNavigationPaths();

	const {
		panelMembers,
		panelMode,
		isLoading: isPanelLoading,
		error,
		hasInvitedMembers,
		hasPendingMembers,
		addMember,
		fetchPanel,
		sendInvitations,
		setMode,
	} = useEvaluationPanel({ rfxId, initialMode: savedPanelMode ?? (hasAIAccess ? undefined : "manual_panel") });

	const {
		isOpen: isDrawerOpen,
		onOpen: onDrawerOpen,
		onClose: onDrawerClose,
	} = useDisclosure();

	const [selectedInviteIds, setSelectedInviteIds] = useState<number[]>([]);
	/**
	 * True only while the "Show Me" tour runs against an EMPTY panel. Substitutes
	 * illustrative rows so the Panel Members step has something to point at instead of
	 * the "No panel members added yet" placeholder. When real members exist we tour the
	 * real rows — they are more useful than fakes.
	 */
	const [showTourPreview, setShowTourPreview] = useState(false);

	/**
	 * Re-checks emptiness on every render, not just when the tour starts: if real
	 * members arrive mid-tour (a slow fetch resolving, or a member accepting), the
	 * preview rows disappear immediately rather than masking real data.
	 */
	const isTourPreview = showTourPreview && panelMembers.length === 0;
	const [isAddingMember, setIsAddingMember] = useState(false);
	const [showPendingWarning, setShowPendingWarning] = useState(false);
	const pendingResolveRef = useRef<((proceed: boolean) => void) | null>(null);

	const handleModeChange = useCallback(
		(mode: PanelMode) => {
			setMode(mode);
			if (onPanelModeChange) {
				onPanelModeChange(mode);
			}
		},
		[setMode, onPanelModeChange]
	);

	const pendingMembers = panelMembers.filter((m) => m.status === "pending");
	const pendingMemberIds = pendingMembers.map((m) => m.id);
	const pendingMemberNames = pendingMembers.map((m) => m.name || m.email);

	const handleToggleSelect = useCallback((memberId: number) => {
		setSelectedInviteIds((prev) => {
			if (prev.includes(memberId)) {
				return prev.filter((id) => id !== memberId);
			}
			return [...prev, memberId];
		});
	}, []);

	const handleToggleSelectAll = useCallback((selectAll: boolean) => {
		setSelectedInviteIds(selectAll ? pendingMemberIds : []);
	}, [pendingMemberIds]);

	const handleSendInvitations = useCallback(
		async (payload: SendInvitationsPayload) => {
			const result = await sendInvitations(payload);
			setSelectedInviteIds([]);
			return result;
		},
		[sendInvitations]
	);

	const checkBeforeNext = useCallback(async (): Promise<boolean> => {
		const isPanelMode = panelMode === "manual_panel" || panelMode === "ai_panel";
		if (isPanelMode && panelMembers.length === 0) {
			toast({
				title: "Add a panel member to continue",
				description:
					"An evaluation panel needs at least one member. Add a member, or switch to scoring it yourself.",
				status: "warning",
				duration: 6000,
				isClosable: true,
			});
			return false;
		}
		if (!hasPendingMembers) {
			return true;
		}
		return new Promise<boolean>((resolve) => {
			pendingResolveRef.current = resolve;
			setShowPendingWarning(true);
		});
	}, [hasPendingMembers, panelMode, panelMembers.length, toast]);

	const handleSendAndContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
		// "Continue Evaluation → Send Invites" sends to everyone not yet invited.
		// Unlike the direct "Send Invitations" button (which uses the user's
		// checkbox selection), no members are selected here — so pre-select ALL
		// pending members. Passing an empty selection would make the drawer's
		// selectedSet filter out everyone and show no recipients.
		setSelectedInviteIds(pendingMemberIds);
		onDrawerOpen();
	};

	const handleWarningContinue = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(true);
			pendingResolveRef.current = null;
		}
	};

	const handleWarningCancel = () => {
		setShowPendingWarning(false);
		if (pendingResolveRef.current) {
			pendingResolveRef.current(false);
			pendingResolveRef.current = null;
		}
	};

	// Register the before-next check with parent
	if (onBeforeNext) {
		onBeforeNext(checkBeforeNext);
	}

	if (isLoading) {
		return (
			<div className="space-y-3">
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
			</div>
		);
	}

	const showMemberManagement = panelMode === "manual_panel" || panelMode === "ai_panel";

	const panelInstructions = [
		"Choose whether to evaluate submissions yourself or assemble an evaluation panel.",
		...(showMemberManagement ? [
			"Add panel members by name or email. Members will receive an invitation to join the evaluation.",
			"You can send invitations now or continue and send them later — uninvited members will be prompted before proceeding.",
		] : []),
	];

	// Panel members never manage the panel. Until the RFx Lead starts Initial
	// scoring they are simply waiting, so show a holding notice (with a link to
	// the RFx details) plus the lead's contact card. Once Initial is reached the
	// step is behind them, so fall back to the read-only panel summary.
	if (!isRfxLead) {
		if (!initialEvaluationReached) {
			const rfxDetailsUrl = rfxId ? clientPaths?.viewRfxDetails(rfxId) : undefined;
			return (
				<div data-testid="evaluation-panel-step" className="space-y-4">
					<InfoBox>
						<p className="font-medium text-gray-900" data-testid="panel-waiting-title">
							The RFx Lead is finalising the Evaluation panel &mdash; you will be
							notified when you are ready to start scoring.
						</p>
						<p className="mt-1 text-sm text-gray-700">
							In the meantime, you can familiarise yourself with the RFx and its
							questions{" "}
							{rfxDetailsUrl ? (
								<a
									href={rfxDetailsUrl}
									className="font-medium text-[#0031DD] underline hover:no-underline"
									data-testid="panel-waiting-rfx-details-link"
								>
									here
								</a>
							) : (
								"here"
							)}
							.
						</p>
					</InfoBox>
					{rfxData?.rfxLead && <RfxLeadInfoBox rfxLead={rfxData.rfxLead} />}
				</div>
			);
		}
		return (
			<div data-testid="evaluation-panel-step" className="space-y-2">
				<PanelMemberSummary members={panelMembers} currentUserId={currentUserId} isLoading={isPanelLoading} />
			</div>
		);
	}

	return (
		<div data-testid="evaluation-panel-step">
			{isRfxLead && (
				<div className="mb-6">
					<InstructionsInfoBox
						instructions={panelInstructions}
						/* Built from the same flags the table renders with, so a step is
						   never emitted for a column that isn't on screen. */
						guideSteps={buildEvaluationPanelSteps({
							canManage: isRfxLead,
							disclaimerEnabled,
						})}
						onBeforeShowMe={() => setShowTourPreview(panelMembers.length === 0)}
						onAfterShowMe={() => setShowTourPreview(false)}
					/>
				</div>
			)}

			{hasAIAccess && (
				<PanelModeSelector
					selectedMode={panelMode}
					hasAIAccess={hasAIAccess}
					isRfxLead={isRfxLead}
					onSelectMode={handleModeChange}
					disabled={!isRfxLead || hasInvitedMembers}
				/>
			)}

			{showMemberManagement && (
				/* data-testid is the Joyride anchor for the "Panel Members" tour step.
				   It must live HERE, not on PanelMemberTable's output: that component
				   renders three mutually-exclusive branches
				   (panel-members-loading / panel-members-empty / panel-members-table),
				   so a tour targeting the table stalls whenever the panel is empty —
				   which is the normal state when a lead first opens this step. */
				<div className="space-y-6" data-testid="panel-members-section">
					<PanelMemberTable
						members={isTourPreview ? TOUR_PREVIEW_MEMBERS : panelMembers}
						rfxId={rfxId}
						evalId={evalId ?? 0}
						isLoading={isPanelLoading}
						canManage={isRfxLead}
						disclaimerEnabled={disclaimerEnabled}
						onMemberChanged={fetchPanel}
						selectedIds={isTourPreview ? [] : selectedInviteIds}
						/* Preview rows must be display-only: PanelMemberTable derives
						   showSelection from `!!onToggleSelect`, so withholding these
						   handlers removes the checkboxes entirely and nothing can enter
						   selectedInviteIds or the invite payload. */
						onToggleSelect={isRfxLead && !isTourPreview ? handleToggleSelect : undefined}
						onToggleSelectAll={isRfxLead && !isTourPreview ? handleToggleSelectAll : undefined}
					/>
					{isRfxLead && isAddingMember && (
						<AddPanelMemberRow
							companyId={companyId}
							currentUserId={currentUserId}
							panelMembers={panelMembers}
							panelMode={panelMode}
							rfxId={rfxId}
							onAddMember={addMember}
							onClose={() => setIsAddingMember(false)}
						/>
					)}
					{/* Persistent guidance beats a transient toast: checkBeforeNext() toasts
					    on click, but the message disappears and the page never says what
					    to do. This states the precondition up-front, and names BOTH ways
					    out (add a member, or switch to scoring it yourself). */}
					{isRfxLead && !isTourPreview && panelMembers.length === 0 && !isPanelLoading && (
						<div className="mt-4" data-testid="panel-empty-validation">
							<WarningBox>
								<span className="text-sm">
									You need at least one panel member before you can start the
									evaluation. Add a member below
									{hasAIAccess ? ", or choose “I'll score it myself” above" : ""}
									.
								</span>
							</WarningBox>
						</div>
					)}

					{isRfxLead && showMemberManagement && !isAddingMember && (
						<div className="mt-6 flex items-center gap-2">
							<Button
								btntype="secondary"
								onClick={() => setIsAddingMember(true)}
								data-testid="add-panel-member-btn"
							>
								<span className="flex items-center gap-1">
									<FiPlus size={14} />
									Add Panel Member
								</span>
							</Button>
							<Button
								btntype="primary"
								onClick={onDrawerOpen}
								disabled={selectedInviteIds.length === 0}
								data-testid="proceed-invitations-btn"
							>
								Send Invitations{selectedInviteIds.length > 0 ? ` (${selectedInviteIds.length})` : ""}
							</Button>
						</div>
					)}
				</div>
			)}

			{error && (
				<p className="mt-3 text-sm text-red-600" data-testid="panel-error">
					{error}
				</p>
			)}

			{showMemberManagement && (
				<InviteConfirmationDrawer
					isOpen={isDrawerOpen}
					onClose={onDrawerClose}
					rfxId={rfxId}
					panelMembers={panelMembers}
					selectedMemberIds={selectedInviteIds}
					onSendInvitations={handleSendInvitations}
					companyName={companyName}
					rfxTitle={rfxTitle}
				/>
			)}

			<Modal
				isOpen={showPendingWarning}
				onClose={handleWarningCancel}
				title="Unsent Invitations"
				iconType="warning"
				size="md"
				closeOnEsc
				closeOnOverlayClick
				customFooter={
					<div className="flex gap-2">
						<Button btntype="primary" onClick={handleSendAndContinue}>
							Send Invites
						</Button>
						<Button btntype="secondary" onClick={handleWarningContinue}>
							Continue without sending
						</Button>
						<Button btntype="secondary" onClick={handleWarningCancel}>
							Cancel
						</Button>
					</div>
				}
			>
				<Text color="gray.600">
					The following panel members have not been sent their invitations yet:
				</Text>
				<ul className="list-disc pl-5 text-sm text-gray-700">
					{pendingMemberNames.map((name) => (
						<li key={name}>{name}</li>
					))}
				</ul>
				<WarningBox>
					<div className="mb-2">
						They will not be able to participate in the evaluation until
						invitations are sent.
					</div>
					<div>Are you sure you want to continue?</div>
				</WarningBox>
			</Modal>
		</div>
	);
};
